import React, { useState, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface EloScoreDisplayProps {
  eloScore?: number | null;
  previousElo?: number | null;
  isOffTheRecord?: boolean;
}

const EloScoreDisplay: React.FC<EloScoreDisplayProps> = ({ 
  eloScore = 0, 
  previousElo,
  isOffTheRecord 
}) => {
  const [animateHit, setAnimateHit] = useState(false);
  const [prevScore, setPrevScore] = useState<number | null>(null);
  
  // Return null if isOffTheRecord is true
  if (isOffTheRecord) return null;

  const hasIncreased = previousElo !== null && eloScore > previousElo;
  const hasDecreased = previousElo !== null && eloScore < previousElo;
  
  // Detect score changes and trigger hit animation effect
  useEffect(() => {
    if (prevScore !== null && prevScore !== eloScore) {
      // Play the hit animation
      setAnimateHit(true);
      // Reset the animation state after animation completes
      const timer = setTimeout(() => setAnimateHit(false), 300);
      return () => clearTimeout(timer);
    }
    setPrevScore(eloScore);
  }, [eloScore, prevScore]);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={animateHit ? {
        y: [-8, 0],
        scale: [1.1, 1],
        transition: { 
          y: { type: "spring", stiffness: 800, damping: 15 },
          scale: { duration: 0.2 }
        }
      } : { 
        opacity: 1,
        transition: { duration: 0.2 }
      }}
      className={cn(
        "relative flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
        "bg-gradient-to-t from-[#1A1F2C] to-[#2A2F3C]",
        "border border-primary/20 shadow-md",
        hasIncreased && "text-green-400",
        hasDecreased && "text-red-400",
        !hasIncreased && !hasDecreased && "text-primary"
      )}
    >
      {/* Mario-style bouncy aura sparkles */}
      <motion.div
        animate={animateHit ? {
          y: [-5, 0],
          scale: [1.3, 1],
          transition: { 
            type: "spring", 
            stiffness: 800, 
            damping: 12,
            duration: 0.3
          }
        } : {}}
      >
        <Sparkles className="w-3 h-3" />
      </motion.div>
      
      {/* Score with Mario-style pop effect */}
      <motion.span
        animate={animateHit ? {
          scale: [1.2, 1],
          transition: { 
            type: "spring", 
            stiffness: 500, 
            damping: 10,
            duration: 0.3
          }
        } : {}}
      >
        {eloScore.toLocaleString()}
      </motion.span>
      
      {/* Number pop effect when score changes */}
      <AnimatePresence>
        {animateHit && hasIncreased && (
          <motion.div
            initial={{ y: 0, opacity: 1, scale: 0.5 }}
            animate={{ y: -15, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="absolute -top-1 -right-1 text-xs font-bold text-yellow-400"
          >
            +1
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default EloScoreDisplay;